import { autobind } from "core-decorators";
import { Inject } from "typescript-ioc";
import { UiComponent } from "Ui/Scripts/UiComponent";
import { UiComponentFactory } from "Ui/Scripts/UiComponentFactory";
import { EventBinder } from "Events/Scripts/EventBinder";
import { LoggerFactory } from "Logging/Scripts/LoggerFactory";
import { FetchService } from "Async/Scripts/FetchService";
import { INotificationsPhones } from "./INotificationsPhones";
import { Utils } from "Helpers/Scripts/Utils";
import { INotificationsPhoneUrlLinks } from "./INotificationsPhoneUrlLinks";
import { INotificationsPhone } from "./INotificationsPhone";

export class DeliveryNotificationsPhone extends UiComponent {
    private readonly _fetchService: FetchService;

    private readonly NOTIFICATIONS_PHONE_DROP_DOWN_SELECTOR: string =
        "#delivery-notifications-phone";
    private readonly DELIVERY_PAGE: string = "#delivery-page";
    private readonly URL_DATA_KEY: string = "url";

    public static readonly templateId: string = "delivery-notifications-phones-template";

    private _updateNotificationsPhoneUrl: string;
    protected _model: INotificationsPhones;

    constructor(
        @Inject componentFactory: UiComponentFactory,
        @Inject binder: EventBinder,
        @Inject loggerFactory: LoggerFactory,
        @Inject fetchService: FetchService
    ) {
        super(componentFactory, binder, loggerFactory);
        this._fetchService = fetchService;
    }

    public init(): void {
        this._setOptions();
        this._setPhoneDropDownList();
    }

    protected _setPhoneDropDownList(): void {
        const data: kendo.ui.DropDownListOptions = {
            change: this._handlePhoneChange,
            dataSource: this._model.NotificationsPhones,
            dataTextField: "Text",
            dataValueField: "Value",
        };

        const selectElement = Utils.find(
            this._context,
            this.NOTIFICATIONS_PHONE_DROP_DOWN_SELECTOR
        );
        if (selectElement.length) {
            const phonesDropDown = kendo.createDropDownList(selectElement[0], data);

            phonesDropDown.select(
                (dataItem: INotificationsPhone) =>
                    dataItem.NotificationsPhoneId === this._model.SelectedNotificationsPhoneId
            );
        }
    }

    // removes selection only on frontend
    public clearPhoneDropDownListSelection(context?: Element): void {
        const dropDownSelector = context
            ? Utils.find(context, this.NOTIFICATIONS_PHONE_DROP_DOWN_SELECTOR)[0]
            : this.NOTIFICATIONS_PHONE_DROP_DOWN_SELECTOR;
        const phonesDropDown = kendo.getDropDownList(dropDownSelector);
        if (phonesDropDown) {
            phonesDropDown.select(0);
        }
    }

    // removes selection on frontend and backend
    public resetPhoneSelection(context?: Element): void {
        const dropDownSelector = context
            ? Utils.find(context, this.NOTIFICATIONS_PHONE_DROP_DOWN_SELECTOR)[0]
            : this.NOTIFICATIONS_PHONE_DROP_DOWN_SELECTOR;
        const phonesDropDown = kendo.getDropDownList(dropDownSelector);
        if (phonesDropDown) {
            phonesDropDown.select(0);
        }
        this._updateNotificationsPhone("");
    }

    private _setOptions(): void {
        const page = this.closest(this.DELIVERY_PAGE);
        if (!page) {
            return;
        }

        const { updateNotificationsPhoneNumber = "" }: INotificationsPhoneUrlLinks = Utils.getData(
            page,
            this.URL_DATA_KEY
        );

        this._updateNotificationsPhoneUrl = updateNotificationsPhoneNumber;
    }

    @autobind
    private _handlePhoneChange(event: kendo.ui.DropDownListChangeEvent): void {
        const phoneId = event.sender.value();
        this._updateNotificationsPhone(phoneId);
    }

    private _updateNotificationsPhone(phoneId: string): void {
        const postData = new FormData();
        postData.append("telephoneId", phoneId);

        this._fetchService.post(this._updateNotificationsPhoneUrl, postData).then(
            () => {
                this._logger.info("Delivery Notifications phone posted successfully.");
            },
            (error) => {
                this._logger.error("Delivery notifications phone post failed - %s", error);
            }
        );
    }
}
